import { KeyRound, LogOut, Route, User as UserIcon } from 'lucide-react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { ROLE_LABELS } from '@cfl/shared';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarTrigger,
} from '@/components/cargofl-ui';
import { ChangePasswordDialog } from '@/components/shell/change-password-dialog';
import { NotificationBell } from '@/components/shell/notification-bell';
import { ThemeToggle } from '@/components/shell/theme-toggle';
import { WelcomeTour, useWelcomeTour } from '@/components/shell/welcome-tour';
import { pageMeta } from '@/lib/page-meta';
import { useAuthConfig, useCurrentUser, useSignOut } from '@/lib/session';

function initials(label: string): string {
  const parts = label.trim().split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase();
}

export function TopBar({ actions }: { actions?: React.ReactNode }) {
  const { pathname } = useLocation();
  const user = useCurrentUser();
  const signOut = useSignOut();
  const { title, subtitle } = pageMeta(pathname);
  const tour = useWelcomeTour();
  const { passwordLogin } = useAuthConfig();
  const [changingPassword, setChangingPassword] = React.useState(false);

  return (
    <header className="sticky top-0 z-30 flex h-[60px] items-center gap-4 border-b bg-background px-4 md:px-6">
      <SidebarTrigger />

      <div className="min-w-0 flex-1">
        <h1 className="truncate text-base font-bold tracking-[-0.01em]">{title}</h1>
        {subtitle ? (
          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>

      {/* Pages inject their own primary action here (Assign goals, Open quarter, ...)
          so there is one CTA slot in the app rather than a button per page header. */}
      {actions}

      <NotificationBell />
      <ThemeToggle />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button hint={"Your profile and sign out"} variant="ghost" className="flex items-center gap-2 px-2">
            <Avatar className="size-7">
              {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt={user.name} /> : null}
              <AvatarFallback className="text-xs">{initials(user.name)}</AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-medium md:inline">{user.name}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span className="truncate font-medium">{user.name}</span>
              <span className="truncate text-xs font-normal text-muted-foreground">
                {user.email}
              </span>
              {/* Both are shown because they answer different questions: what this
                  person's job is, and what the app will let them do. */}
              <span className="mt-1 truncate text-xs font-normal text-muted-foreground">
                {user.designationTitle
                  ? `${user.designationTitle} · ${ROLE_LABELS[user.role]}`
                  : ROLE_LABELS[user.role]}
                {user.departmentName ? ` · ${user.departmentName}` : ''}
              </span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled>
            <UserIcon className="size-4" />
            Profile
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => tour.setOpen(true)}>
            <Route className="size-4" />
            Take the tour
          </DropdownMenuItem>
          {/* Only for someone who signs in with a password: a Google-only account has no
              current password to give, and the API refuses the route when it is off. */}
          {passwordLogin && user.hasPassword ? (
            <DropdownMenuItem onSelect={() => setChangingPassword(true)}>
              <KeyRound className="size-4" />
              Change password
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={() => void signOut()}>
            <LogOut className="size-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <WelcomeTour open={tour.open} onOpenChange={tour.setOpen} />
      <ChangePasswordDialog open={changingPassword} onOpenChange={setChangingPassword} />
    </header>
  );
}
